import api from "./api";

const userService = {
  // Récupérer tous les utilisateurs avec filtres et pagination
  getUsers: async (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `/users?${queryString}` : "/users";

    return await api.get(url);
  },

  // Récupérer les professionnels (accessible à tous les rôles authentifiés)
  getProfessionals: async (params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/professionals?${queryString}`
      : "/users/professionals";
    return await api.get(url);
  },

  // Récupérer un utilisateur par ID
  getUserById: async (id) => {
    return await api.get(`/users/${id}`);
  },

  // Créer un nouvel utilisateur (admin)
  createUser: async (userData) => {
    return await api.post("/users", userData);
  },

  // Mettre à jour un utilisateur
  updateUser: async (id, userData) => {
    return await api.put(`/users/${id}`, userData);
  },

  // Supprimer un utilisateur
  deleteUser: async (id) => {
    return await api.delete(`/users/${id}`);
  },

  // Télécharger un avatar
  uploadAvatar: async (id, formData) => {
    return await api.put(`/users/${id}/avatar`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Télécharger les documents légaux du professionnel (CNI, casier)
  uploadProfessionalDocuments: async (id, formData) => {
    return await api.put(`/users/${id}/professional-documents`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // Récupérer les statistiques des utilisateurs
  getUserStats: async () => {
    return await api.get("/users/stats");
  },

  // Récupérer les statistiques d'un utilisateur spécifique
  getUserStatsById: async (id) => {
    return await api.get(`/users/${id}/stats`);
  },

  // Rechercher des utilisateurs par localisation
  searchByLocation: async (params) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    return await api.get(`/users/search/location?${queryParams.toString()}`);
  },

  // Bloquer un utilisateur
  blockUser: async (id) => {
    return await api.put(`/users/${id}/block`);
  },

  // Débloquer un utilisateur
  unblockUser: async (id) => {
    return await api.put(`/users/${id}/unblock`);
  },

  // Vérifier un utilisateur (admin)
  verifyUser: async (id) => {
    return await api.put(`/users/${id}/verify`);
  },

  // Annuler la vérification d'un utilisateur (admin)
  unverifyUser: async (id) => {
    return await api.put(`/users/${id}/unverify`);
  },

  // Récupérer l'activité d'un utilisateur
  getUserActivity: async (id, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${id}/activity?${queryString}`
      : `/users/${id}/activity`;

    return await api.get(url);
  },

  // Récupérer le profil d'un utilisateur
  getUserProfile: async (id) => {
    return await api.get(`/users/${id}/profile`);
  },

  // Rechercher des utilisateurs (recherche textuelle)
  searchUsers: async (query, params = {}) => {
    const queryParams = new URLSearchParams({ q: query });

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    return await api.get(`/users/search?${queryParams.toString()}`);
  },

  // Récupérer les utilisateurs favoris
  getFavoriteUsers: async () => {
    return await api.get("/users/favorites");
  },

  // Ajouter un utilisateur aux favoris
  addToFavorites: async (id) => {
    return await api.post(`/users/${id}/favorite`);
  },

  // Retirer un utilisateur des favoris
  removeFromFavorites: async (id) => {
    return await api.delete(`/users/${id}/favorite`);
  },

  // Récupérer les avis sur un utilisateur
  getUserReviews: async (id) => {
    return await api.get(`/users/${id}/reviews`);
  },

  // Ajouter un avis sur un utilisateur
  addUserReview: async (id, reviewData) => {
    return await api.post(`/users/${id}/review`, reviewData);
  },

  // Nouveaux endpoints pour le profil utilisateur complet
  // Récupérer le profil complet de l'utilisateur connecté
  getMyProfile: async () => {
    return await api.get("/auth/me");
  },

  // Récupérer les missions créées par l'utilisateur
  getUserCreatedMissions: async (id, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${id}/missions/created?${queryString}`
      : `/users/${id}/missions/created`;

    return await api.get(url);
  },

  // Récupérer les missions auxquelles l'utilisateur a participé
  getUserParticipatedMissions: async (id, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${id}/missions/participated?${queryString}`
      : `/users/${id}/missions/participated`;

    return await api.get(url);
  },

  // Récupérer les statistiques des missions de l'utilisateur
  getUserMissionStats: async (id) => {
    return await api.get(`/users/${id}/missions/stats`);
  },

  // Récupérer les candidatures de l'utilisateur
  getUserApplications: async (id, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${id}/applications?${queryString}`
      : `/users/${id}/applications`;

    return await api.get(url);
  },

  // Récupérer les statistiques des candidatures de l'utilisateur
  getUserApplicationStats: async (id) => {
    return await api.get(`/users/${id}/applications/stats`);
  },

  // Récupérer les candidatures reçues par l'utilisateur
  getUserReceivedApplications: async (id, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${id}/applications/received?${queryString}`
      : `/users/${id}/applications/received`;

    return await api.get(url);
  },

  // Récupérer les messages de l'utilisateur
  getUserMessages: async (id, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${id}/messages?${queryString}`
      : `/users/${id}/messages`;

    return await api.get(url);
  },

  // Récupérer les notifications de l'utilisateur
  getUserNotifications: async (id, params = {}) => {
    const queryParams = new URLSearchParams();

    Object.keys(params).forEach((key) => {
      if (
        params[key] !== undefined &&
        params[key] !== null &&
        params[key] !== ""
      ) {
        queryParams.append(key, params[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString
      ? `/users/${id}/notifications?${queryString}`
      : `/users/${id}/notifications`;

    return await api.get(url);
  },

  // Récupérer les statistiques de communication de l'utilisateur
  getUserCommunicationStats: async (id) => {
    return await api.get(`/users/${id}/communication/stats`);
  },
};

export default userService;
